import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { useDropDirection } from "src/hooks/useDropDirection";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { TbX, TbPlus, TbAlertTriangle, TbStethoscope, TbBulb } from "react-icons/tb";
import { useApi } from "src/hooks/useApi";
import { apiRequest } from "src/services/apiClient";
import { fold, foldExpanded } from "src/helpers/textFold";
import type { Doctor } from "src/types";
import type { InvoiceDraftData } from "./invoiceDraft";
import { addDiagnosis, addTreatmentDate, formatDay, type TreatmentIssue } from "./treatmentRules";
import { CERTAINTIES, SIDES, formatDiagnosis, parseDiagnosis, sideIsCommon, type ParsedDiagnosis } from "./diagnosisText";
import type { RelatedDiagnosis } from "./suggestions";

export const doctorName = (d: Pick<Doctor, "salutation" | "title" | "firstName" | "lastName">) =>
    [d.salutation, d.title, d.firstName, d.lastName].filter(Boolean).join(" ");

interface OwnerChoice {
    doctors: Doctor[];
    showPicker: boolean;
    /** A doctor/admin with "create for other doctors" — the pick is optional, defaulting to them. */
    mayPickOthers: boolean;
    ownerId: string;
    owner: Doctor | null;
}

/** Step 2: who the invoice belongs to, invoice date, treatment days and diagnoses. */
export function StepTreatment({
    draft,
    update,
    owner,
    today,
    issues,
    related = [],
}: {
    draft: InvoiceDraftData;
    update: (patch: Partial<InvoiceDraftData>) => void;
    owner: OwnerChoice;
    today: string;
    issues: TreatmentIssue[];
    /** Plus+: diagnoses the practice often enters together with the ones here. */
    related?: RelatedDiagnosis[];
}) {
    const { t } = useTranslation();
    const [newDay, setNewDay] = useState("");
    const has = (issue: TreatmentIssue) => issues.includes(issue);

    return (
        <div className="ic-treatment">
            <section className="ic-field-group" aria-labelledby="ic-owner-label">
                <h3 id="ic-owner-label" className="ic-field-group__title">{t("invoiceCreate.treatment.doctor")}</h3>
                {owner.showPicker ? (
                    <div className="ic-field">
                        <div
                            className={`ic-owner-chips${has("ownerMissing") ? " ic-owner-chips--error" : ""}`}
                            role="radiogroup"
                            aria-labelledby="ic-owner-label"
                            aria-invalid={has("ownerMissing")}
                        >
                            {owner.doctors.map((d) => {
                                const selected = owner.ownerId === d.id;
                                return (
                                    <button
                                        key={d.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected}
                                        className={`ic-owner-chip${selected ? " ic-owner-chip--selected" : ""}`}
                                        onClick={() => update({ ownerPick: d.id })}
                                    >
                                        <TbStethoscope aria-hidden /> {doctorName(d)}
                                    </button>
                                );
                            })}
                        </div>
                        <span className="ic-field__hint">
                            {t(owner.mayPickOthers ? "invoiceCreate.treatment.doctorOthersHint" : "invoiceCreate.treatment.doctorStaffHint")}
                        </span>
                    </div>
                ) : owner.owner ? (
                    <div className="ic-owner">
                        <TbStethoscope aria-hidden />
                        <span className="ic-owner__name">{doctorName(owner.owner)}</span>
                    </div>
                ) : (
                    <span className="ic-field__hint">{t("invoiceCreate.treatment.doctorLoading")}</span>
                )}
            </section>

            <section className="ic-field-group" aria-labelledby="ic-days-label">
                    <h3 id="ic-days-label" className="ic-field-group__title">{t("invoiceCreate.treatment.days")}</h3>
                    <ul className="ic-days" aria-labelledby="ic-days-label">
                        {draft.treatmentDates.map((d) => {
                            const late = d > today;
                            return (
                                <li key={d} className={`ic-day${late ? " ic-day--error" : ""}`}>
                                    <span>{formatDay(d)}</span>
                                    <button
                                        type="button"
                                        className="ic-day__remove"
                                        onClick={() => update({ treatmentDates: draft.treatmentDates.filter((x) => x !== d) })}
                                        aria-label={t("invoiceCreate.treatment.removeDay", { date: formatDay(d) })}
                                        title={t("invoiceCreate.treatment.removeDay", { date: formatDay(d) })}
                                    >
                                        <TbX aria-hidden />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="ic-days__add">
                        <input
                            type="date"
                            className="ic-input ic-input--date"
                            value={newDay}
                            max={today}
                            onChange={(e) => setNewDay(e.target.value)}
                            aria-label={t("invoiceCreate.treatment.newDay")}
                        />
                        <button
                            type="button"
                            className="ic-secondary-btn"
                            disabled={!newDay}
                            onClick={() => {
                                update({ treatmentDates: addTreatmentDate(draft.treatmentDates, newDay) });
                                setNewDay("");
                            }}
                        >
                            <TbPlus aria-hidden /> {t("invoiceCreate.treatment.addDay")}
                        </button>
                    </div>
                    {(has("noTreatmentDay") || has("treatmentInFuture")) && (
                        <span className="ic-field__hint ic-field__hint--error" role="alert">
                            {has("noTreatmentDay") ? t("invoiceCreate.treatment.noTreatmentDay") : t("invoiceCreate.treatment.treatmentInFuture")}
                        </span>
                    )}
                    {!has("noTreatmentDay") && !has("treatmentInFuture") && (
                        <span className="ic-field__hint">{t("invoiceCreate.treatment.daysHint")}</span>
                    )}
            </section>

            <DiagnosisInput diagnoses={draft.diagnoses} onChange={(diagnoses) => update({ diagnoses })} missing={has("noDiagnosis")} related={related} />
        </div>
    );
}

type IcdHit = { code: string; title: string; term?: string };
type DiagnosisOption = { key: string; value: string } & ({ kind: "known" } | { kind: "icd"; hit: IcdHit });

/** ICD-10-GM search (GET /icd/search) once the typing pauses; from 2 characters on. */
function useIcdSearch(text: string) {
    const query = text.trim();
    const [result, setResult] = useState<{ query: string; version: number | null; results: IcdHit[] } | null>(null);
    useEffect(() => {
        if (query.length < 2) return;
        let cancelled = false;
        const timer = window.setTimeout(() => {
            apiRequest<{ version: number | null; results: IcdHit[] }>(`/icd/search?q=${encodeURIComponent(query)}`)
                .then((res) => !cancelled && setResult({ query, ...res }))
                .catch(() => !cancelled && setResult({ query, version: null, results: [] }));
        }, 200);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [query]);
    return result && result.query === query ? result : { version: null, results: [] as IcdHit[] };
}

function DiagnosisInput({
    diagnoses,
    onChange,
    missing,
    related,
}: {
    diagnoses: string[];
    onChange: (diagnoses: string[]) => void;
    missing: boolean;
    related: RelatedDiagnosis[];
}) {
    const { t } = useTranslation();
    const listId = useId();
    const { data } = useApi<{ diagnoses: { text: string; count: number }[] }>("/invoices/diagnoses-suggest");
    const [text, setText] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const entryRef = useRef<HTMLDivElement>(null);
    const icd = useIcdSearch(text);

    // The practice's own diagnoses first, then ICD-10-GM matches (codes not already entered or listed).
    const options = useMemo(() => {
        const enteredCodes = new Set(diagnoses.map((d) => parseDiagnosis(d).code).filter(Boolean));
        const known = (data?.diagnoses ?? []).filter((d) => {
            const code = parseDiagnosis(d.text).code;
            return !diagnoses.some((x) => x.toLowerCase() === d.text.toLowerCase()) && !(code && enteredCodes.has(code));
        });
        const tokens = text.trim().split(/\s+/).filter(Boolean);
        const matches = (s: string) => tokens.every((tok) => fold(s).includes(fold(tok)) || foldExpanded(s).includes(foldExpanded(tok)));
        const own: DiagnosisOption[] = (tokens.length === 0 ? known.slice(0, 6) : known.filter((d) => matches(d.text)).slice(0, 6))
            .map((d) => ({ kind: "known", key: `k|${d.text}`, value: d.text }));
        const ownCodes = new Set(own.map((o) => parseDiagnosis(o.value).code).filter(Boolean));
        const fromIcd: DiagnosisOption[] = tokens.length === 0 ? [] : icd.results
            .filter((h) => !enteredCodes.has(h.code) && !ownCodes.has(h.code))
            .map((h) => ({ kind: "icd", key: `i|${h.code}`, value: `${h.code} ${h.title}`, hit: h }));
        return [...own, ...fromIcd];
    }, [data, diagnoses, text, icd.results]);
    const firstIcd = options.findIndex((o) => o.kind === "icd");

    // Closes the list — with the field empty it would otherwise jump to "Häufig verwendet" under the cursor.
    const add = (value: string) => {
        onChange(addDiagnosis(diagnoses, value));
        setText("");
        setActive(-1);
        setOpen(false);
    };
    const replaceAt = (index: number, value: string) => onChange(diagnoses.map((d, i) => (i === index ? value : d)));

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown" && options.length > 0) {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, options.length - 1));
        } else if (e.key === "ArrowUp" && options.length > 0) {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (open && active >= 0 && options[active]) add(options[active].value);
            else add(text);
        } else if (e.key === "Escape") {
            setOpen(false);
            setActive(-1);
        }
    };

    const showList = open && options.length > 0;
    const direction = useDropDirection(entryRef, showList);
    const relatedOpen = related.filter((r) => addDiagnosis(diagnoses, r.text) !== diagnoses);

    return (
        <section className="ic-field-group ic-diagnoses" aria-labelledby="ic-diagnoses-label">
            <h3 className="ic-field-group__title">
                <label id="ic-diagnoses-label" htmlFor="ic-diagnosis-input">{t("invoiceCreate.treatment.diagnoses")}</label>
            </h3>

            {diagnoses.length > 0 && (
                <ol className="ic-diagnoses__list">
                    {diagnoses.map((d, index) => {
                        const parsed = parseDiagnosis(d);
                        return (
                            <li key={parsed.code ?? d} className="ic-diagnoses__item">
                                <span className="ic-diagnoses__text">
                                    {parsed.code && <><strong className="ic-diagnoses__code">{parsed.code}</strong>{" "}</>}
                                    {parsed.title}
                                </span>
                                {parsed.code && (
                                    <DiagnosisMarkers
                                        parsed={parsed}
                                        label={d}
                                        onChange={(next) => replaceAt(index, formatDiagnosis(next))}
                                    />
                                )}
                                <button
                                    type="button"
                                    className="ic-day__remove"
                                    onClick={() => onChange(diagnoses.filter((x) => x !== d))}
                                    aria-label={t("invoiceCreate.treatment.removeDiagnosis", { text: d })}
                                    title={t("invoiceCreate.treatment.removeDiagnosis", { text: d })}
                                >
                                    <TbX aria-hidden />
                                </button>
                            </li>
                        );
                    })}
                </ol>
            )}

            {relatedOpen.length > 0 && (
                <div className="ic-diagnoses__related" role="group" aria-labelledby="ic-related-label">
                    <span id="ic-related-label" className="ic-diagnoses__related-title">
                        <TbBulb aria-hidden /> {t("invoiceCreate.treatment.relatedTitle")}
                    </span>
                    {relatedOpen.map((r) => {
                        const why = t(`invoiceCreate.treatment.relatedReason.${r.reason.key.split(".")[1]}`, r.reason.params);
                        return (
                            <button
                                key={r.text}
                                type="button"
                                className="ic-diagnoses__related-btn"
                                onClick={() => add(r.text)}
                                title={why}
                                aria-label={`${t("invoiceCreate.treatment.relatedAdd", { text: r.text })} – ${why}`}
                            >
                                <TbPlus aria-hidden /> {r.text}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="ic-diagnoses__entry" ref={entryRef}>
                <input
                    id="ic-diagnosis-input"
                    className="ic-input"
                    aria-required
                    aria-invalid={missing}
                    aria-describedby="ic-diagnosis-hint"
                    value={text}
                    maxLength={200}
                    placeholder={t("invoiceCreate.treatment.diagnosisPlaceholder")}
                    onChange={(e) => {
                        setText(e.target.value);
                        setOpen(true);
                        setActive(-1);
                    }}
                    onFocus={() => setOpen(true)}
                    onClick={() => setOpen(true)}
                    onBlur={() => window.setTimeout(() => setOpen(false), 150)}
                    onKeyDown={onKeyDown}
                    role="combobox"
                    aria-expanded={showList}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
                    autoComplete="off"
                />
                <button type="button" className="ic-secondary-btn" disabled={!text.trim()} onClick={() => add(text)}>
                    <TbPlus aria-hidden /> {t("invoiceCreate.treatment.addDiagnosis")}
                </button>
                {showList && (
                    <ul id={listId} role="listbox" className={`ic-suggest${direction === "up" ? " ic-suggest--up" : ""}`} aria-label={t("invoiceCreate.treatment.diagnosisSuggestions")}>
                        {!text.trim() && <li className="ic-suggest__caption" role="presentation">{t("invoiceCreate.treatment.frequentDiagnoses")}</li>}
                        {options.map((o, i) => (
                            <Fragment key={o.key}>
                                {i === firstIcd && <li className="ic-suggest__caption" role="presentation">{t("invoiceCreate.treatment.icdCaption")}</li>}
                                <li
                                    id={`${listId}-${i}`}
                                    role="option"
                                    aria-selected={i === active}
                                    className={`ic-suggest__option${i === active ? " ic-suggest__option--active" : ""}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        add(o.value);
                                    }}
                                    onMouseEnter={() => setActive(i)}
                                >
                                    {o.kind === "icd" ? (
                                        <>
                                            <strong className="ic-suggest__code">{o.hit.code}</strong> {o.hit.title}
                                            {o.hit.term && <span className="ic-suggest__term">{t("invoiceCreate.treatment.icdTerm", { term: o.hit.term })}</span>}
                                        </>
                                    ) : (
                                        o.value
                                    )}
                                </li>
                            </Fragment>
                        ))}
                        {firstIcd >= 0 && icd.version && (
                            <li className="ic-suggest__source" role="presentation">
                                {t("invoiceCreate.treatment.icdSource", { version: icd.version })}{" "}
                                {/* BfArM download terms: source note required (§ 63 UrhG) — full texts on the Docs page. */}
                                <a href="/docs?section=sources" target="_blank" rel="noopener noreferrer" onMouseDown={(e) => e.preventDefault()}>
                                    {t("invoiceCreate.treatment.icdSourceLink")}
                                </a>
                            </li>
                        )}
                    </ul>
                )}
            </div>
            <span className={`ic-field__hint${missing ? " ic-field__hint--error" : ""}`} id="ic-diagnosis-hint">
                {t(missing ? "invoiceCreate.treatment.noDiagnosis" : "invoiceCreate.treatment.diagnosisHint")}
            </span>
        </section>
    );
}

/** Certainty (G/V/A/Z, always one) and side (R/L/B, optional — click again to clear) of a coded diagnosis.
 *  The side shows up front only where it's common (`sideIsCommon`) or already set; elsewhere behind "+ Seite". */
function DiagnosisMarkers({ parsed, label, onChange }: { parsed: ParsedDiagnosis; label: string; onChange: (next: ParsedDiagnosis) => void }) {
    const { t } = useTranslation();
    const [sideOpened, setSideOpened] = useState(false);
    const showSide = sideOpened || !!parsed.side || (!!parsed.code && sideIsCommon(parsed.code));
    return (
        <span className="ic-diagnoses__markers">
            <span className="ic-marker-group" role="radiogroup" aria-label={`${t("invoiceCreate.treatment.certainty")}: ${label}`}>
                {CERTAINTIES.map((c) => (
                    <button
                        key={c}
                        type="button"
                        role="radio"
                        aria-checked={parsed.certainty === c}
                        className={`ic-marker${parsed.certainty === c ? " ic-marker--on" : ""}${c === "A" ? " ic-marker--negative" : ""}`}
                        title={t(`invoiceCreate.treatment.certaintyNames.${c}`)}
                        aria-label={t(`invoiceCreate.treatment.certaintyNames.${c}`)}
                        onClick={() => onChange({ ...parsed, certainty: c })}
                    >
                        {c}
                    </button>
                ))}
            </span>
            {!showSide ? (
                <button
                    type="button"
                    className="ic-marker-add"
                    aria-label={`${t("invoiceCreate.treatment.addSide")}: ${label}`}
                    onClick={() => setSideOpened(true)}
                >
                    + {t("invoiceCreate.treatment.side")}
                </button>
            ) : (
                <span className="ic-marker-group" role="group" aria-label={`${t("invoiceCreate.treatment.side")}: ${label}`}>
                    {SIDES.map((s) => (
                        <button
                            key={s}
                            type="button"
                            aria-pressed={parsed.side === s}
                            className={`ic-marker${parsed.side === s ? " ic-marker--on" : ""}`}
                            title={t(`invoiceCreate.treatment.sideNames.${s}`)}
                            aria-label={t(`invoiceCreate.treatment.sideNames.${s}`)}
                            onClick={() => onChange({ ...parsed, side: parsed.side === s ? null : s })}
                        >
                            {s}
                        </button>
                    ))}
                </span>
            )}
        </span>
    );
}

/** Text for the step's card in the overview row. */
export function treatmentSummary(draft: InvoiceDraftData, owner: Doctor | null, t: TFunction) {
    const days = draft.treatmentDates;
    return (
        <>
            {owner && <strong className="ic-card__strong">{doctorName(owner)}</strong>}
            <span>
                {days.length === 1
                    ? t("invoiceCreate.treatment.summaryOneDay", { date: formatDay(days[0]) })
                    : t("invoiceCreate.treatment.summaryDays", { count: days.length })}
            </span>
            <span>{t("invoiceCreate.treatment.summaryDiagnoses", { count: draft.diagnoses.length })}</span>
        </>
    );
}
